import React, { useState, useEffect } from 'react';
import { createDonation, queryFamilyForDonation } from "../../service/DonationApi";
import { queryCategoryConfig } from "../../service/UtilApi";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { useCheckAccess, getCurrentDate } from "../../utils/Utilities";
import { useNavigate } from "react-router-dom";
import '../general/General.css';
// Function to get today's date
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Function to generate unique transaction ID
const generateTransactionId = ({ familyId, category, subcategory, uniqueOption }) => {
  if (!familyId || !category || !subcategory) {
    throw new Error("Missing required input to generate transaction ID");
  }
  const baseId = `${familyId}_${category}_${subcategory}`.replace(/\s+/g, '');
  if (uniqueOption === 'familyId') {
    return baseId;
  }
  const currentDate = getCurrentDate();
  return `${baseId}_${currentDate}`;
};


// Main component for donation creation
const DonationCreate = () => {
  useCheckAccess('DonationRW');

  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [familyId, setFamilyId] = useState('');
  const [mobile, setMobile] = useState('');
  const [donationDate, setDonationDate] = useState(getTodayDate());
  const [transactionMode, setTransactionMode] = useState('cash');
  const [externalTransactionReference, setExternalTransactionReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [searchError, setSearchError] = useState('');
  const [validationError, setValidationError] = useState('');
  const navigate = useNavigate();
  const [searching, setSearching] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingDonationData, setPendingDonationData] = useState(null);
  const [selfSponsoredWarning, setSelfSponsoredWarning] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await queryCategoryConfig();
        if (response?.categoriesList?.length > 0) {
          setCategories(response.categoriesList);
        } else {
          setCategoriesError("No categories found");
        }
      } catch {
        setCategoriesError("Failed to load categories");
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const selectedMember = searchResult?.members?.find(m => m.uid === selectedMemberId);
  const selectedCategory = categories.find(cat => cat.categoryName === categoryName);

  const sortedSubcategories = selectedCategory?.subcategories
    ? [...selectedCategory.subcategories].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const isValidSearchInput = () => {
    if (!familyId && !mobile) {
      setValidationError("தயவுசெய்து குடும்ப எண், Mobile அல்லது Aadhaar ஒன்றை உள்ளிடவும்.");
      return false;
    }

    if (familyId && !/^\d{1,4}$/.test(familyId)) {
      setValidationError("குடும்ப எண் 1 முதல் 4 இலக்க எண் இருக்க வேண்டும்.");
      return false;
    }

    if (mobile && !/^\d{10}$/.test(mobile)) {
      setValidationError("மொபைல் எண் 10 இலக்க எண் இருக்க வேண்டும்.");
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSearchFamily = async () => {
    setSearchResult(null);
    setSearchError('');
    setSelectedMemberId('');
    setSearching(true);
    setSelectedSubcategories([]); // 🔁 Reset subcategories
    setCategoryName('');          // 🔁 Reset category

    if (!isValidSearchInput()) {
      setSearching(false);
      return;
    }

    try {
      const response = await queryFamilyForDonation({ familyId, mobile });

      if (response?.family || response?.members?.length > 0) {
        setSearchResult(response);
        if (response.family?.id) {
          setFamilyId(response.family.id);
        }
      } else {
        setSearchError("No matching family found.");
      }
    } catch (err) {
      setSearchError("Error fetching family data.");
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddDonation = async () => {
    if (!selectedMember) {
      alert("தயவுசெய்து ஒரு குடும்ப உறுப்பினரை தேர்வு செய்யவும்.");
      return;
    }

    if (!categoryName) {
      alert("தயவுசெய்து ஒரு வகையை தேர்வு செய்யவும்.");
      return;
    }

    if (selectedSubcategories.length === 0) {
      alert("தயவுசெய்து ஒரு துணை வகையைத் தேர்ந்தெடுக்கவும்.");
      return;
    }

    // ❗ Validate amounts
    for (const sub of selectedSubcategories) {
      if (!sub.amount || isNaN(sub.amount)) {
        alert("தயவுசெய்து அனைத்து துணை வகைகளுக்கும் நன்கொடை தொகையை உள்ளிடவும்.");
        return;
      }

      const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
      const matchedDonations = searchResult?.donations?.filter(d =>
        d.category === selectedCategory.description &&
        d.subcategory === fullSub.description &&
        (fullSub.uniqueOption === 'familyId' || d.familyMemberFirstName === selectedMember.firstName)
      ) || [];

      const totalDonated = matchedDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      const proposedTotal = totalDonated + parseFloat(sub.amount);

      console.log('fullSub.amount', fullSub.amount);
      if (fullSub.amount && proposedTotal > fullSub.amount) {
        alert(`❗ துணை வகை "${fullSub.description}" இல் அதிகபட்ச தொகை ₹${fullSub.amount}. ஏற்கனவே செலுத்தியது ₹${totalDonated}. நீங்கள் ₹${parseFloat(sub.amount)} செலுத்த முயற்சிக்கிறீர்கள்.`);
        return;
      }

    }
    // ✅ Ask for confirmation
    // Check if any selected subcategory allows self-sponsored but user didn’t select it
    const needsSelfSponsoredPrompt = selectedSubcategories.some(sub => {
      const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
      return fullSub?.allowSelfSponsored && !sub.isSelfSponsored;
    });

    setSelfSponsoredWarning(needsSelfSponsoredPrompt);
    setPendingDonationData({
      familyId,
      familyMemberId: selectedMember.uid,
      familyMemberFirstName: selectedMember.firstName,
      category: categoryName,
      categoryDescription: selectedCategory?.description || '',
      date: donationDate,
      externalTransactionReference,
      transactionMode,
      subcategories: selectedSubcategories.map(sub => {
        const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
        return {
          subcategory: sub.subcategory,
          subCategoryDescription: fullSub?.description || '',
          amount: Number(sub.amount),
          isSelfSponsored: !!sub.isSelfSponsored,
          transactionId: sub.transactionId || generateTransactionId({
            familyId,
            category: categoryName,
            subcategory: sub.subcategory,
            uniqueOption: sub.uniqueOption,
          }),
        };
      })
    });
    setShowConfirmationModal(true);
  };



  const handleSubcategoryChange = (subcategory, checked) => {
    setSelectedSubcategories(prevState => {
      if (checked) {
        // Find matched donations for this subcategory
        const matchedDonations = searchResult?.donations?.filter(d =>
          d.category === selectedCategory?.description &&
          d.subcategory === subcategory.description &&
          (
            subcategory.uniqueOption === 'familyId' ||
            subcategory.uniqueOption === 'familyId_date' ||
            d.familyMemberFirstName === selectedMember?.firstName
          )
        ) || [];

        const totalDonated = matchedDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
        const remaining = Math.max((subcategory.amount || 0) - totalDonated, 0);

        return [
          ...prevState,
          {
            subcategory: subcategory.name,
            amount: subcategory.uniqueOption === 'familyId' ? subcategory.amount : remaining.toFixed(0),
            transactionId: generateTransactionId({
              familyId,
              category: categoryName,
              subcategory: subcategory.name,
              uniqueOption: subcategory.uniqueOption,
              date: donationDate
            }),
            isSelfSponsored: false
          }
        ];
      } else {
        return prevState.filter(sub => sub.subcategory !== subcategory.name);
      }
    });
  };


  const handleAmountChange = (subcategoryName, amount) => {
    const numericAmount = parseFloat(amount);
    setSelectedSubcategories(prevState =>
      prevState.map(sub =>
        sub.subcategory === subcategoryName
          ? { ...sub, amount: isNaN(numericAmount) ? '' : numericAmount }
          : sub
      )
    );
  };

  const toggleSelfSponsored = (subcategoryName, checked) => {
    setSelectedSubcategories(prev =>
      prev.map(sub =>
        sub.subcategory === subcategoryName
          ? { ...sub, isSelfSponsored: checked }
          : sub
      )
    );
  };

  return (
    <>
      <Navbar />
      <Helmet>
        <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | Donation Create</title>
        <link rel="canonical" href="/donation/create" />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <h2>நன்கொடை உருவாக்கு</h2>

            <div className="auth_element">
              <label className="auth_label">குடும்ப எண்:</label>
              <input value={familyId} onChange={e => setFamilyId(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="4"
              />

              <label className="auth_label">மொபைல்:</label>
              <input value={mobile} onChange={e => setMobile(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="10" />

              {searching ? (
                <Loading />
              ) : (
                <button onClick={handleSearchFamily}>தேடு</button>
              )}

              {validationError && <p style={{ color: 'red' }}>{validationError}</p>}
            </div>

            {searchError && <p style={{ color: 'red' }}>{searchError}</p>}

            {searchResult?.family && (
              <>

                <div className="detail-item" style={{
                  border: "1px solid var(--highlight-shadow-color)",
                  padding: "1rem",
                  marginTop: "1rem",

                }}>

                  <span className="detail-label"><strong>குடும்ப எண்:</strong></span> <span className="detail-value">{searchResult.family.id}

                  </span>
                  <span className="detail-label"><strong>கூட்டம்:</strong></span><span className="detail-value"> {searchResult.family.kootamName}
                  </span>
                  <span className="detail-label"><strong>தலைமை பெயர்:</strong> </span><span className="detail-value">{searchResult.family.familyHeadFirstName} {searchResult.family.familyHeadLastName}
                  </span>
                  {searchResult.family.tempAddressName_tamil && (
                    <>
                      <span className="detail-label"><strong>தற்காலிக முகவரி:</strong> </span>
                      <span className="detail-value">{searchResult.family.tempAddressName_tamil}, {searchResult.family.tempAddressBlock_tamil}, {searchResult.family.tempAddressDistrict_tamil}
                      </span>
                    </>
                  )}
                  <span className="detail-label"><strong>நிரந்தர முகவரி:</strong> </span>
                  <span className="detail-value">{searchResult.family.permanentAddressName_tamil}, {searchResult.family.permanentAddressBlock_tamil}, {searchResult.family.permanentAddressDistrict_tamil}
                  </span>
                  <br />
                  <button
                    style={{ marginTop: '1rem' }}
                    onClick={() => navigate(`/donation/view/${searchResult.family.id}`)}
                  >
                    குடும்ப நன்கொடைப் பதிவு பார்க்க
                  </button>

                </div>
                {searchResult.family.status !== 'active' && (
                  <div style={{ marginTop: '1rem', color: 'red', fontWeight: 'bold' }}>
                    ⚠️ குடும்ப விவரங்கள் முலுமையாக இல்லை. தயவுசெய்து விண்ணப்பப் படிவத்தை பூர்த்தி செய்யவும்.
                    <br></br>
                  </div>

                )}
              </>
            )}


            {searchResult?.members?.length > 0 && (
              <div className="auth_element" style={{ marginTop: "1rem" }}>
                <h3>குடும்ப உறுப்பினர்கள்:</h3>
                <br />
                {searchResult.members.map(member => (
                  <div key={member.uid}>
                    <label className='auth_radio'>
                      <input
                        type="radio"
                        name="selectedMember"
                        value={member.uid}
                        checked={selectedMemberId === member.uid}
                        onChange={() => setSelectedMemberId(member.uid)}
                      />
                      {member.firstName} {member.lastName}{(member.mobile || member.mobile2) ? ` - 📱 ${member.mobile || ''}${member.mobile2 ? (member.mobile ? ' / ' : '') + member.mobile2 : ''}` : ''}
                    </label>
                    {member.status !== 'active' && (
                      <div style={{ color: 'red', marginLeft: '1.5rem', fontSize: '0.9rem' }}>
                        ⚠️ இந்த உறுப்பினர் ஆதார் விவரங்களை சமர்ப்பிக்கவும்.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedMember && (
              <>
                <div className="auth_element" style={{ marginTop: "2rem" }}>
                  <h4>நன்கொடை வகை:</h4>
                  {categoriesLoading ? (
                    <p>Loading categories...</p>
                  ) : categoriesError ? (
                    <p style={{ color: 'red' }}>{categoriesError}</p>
                  ) : (
                    <select
                      value={categoryName}
                      onChange={(e) => {
                        setCategoryName(e.target.value);
                        setSelectedSubcategories([]);
                      }}
                      className="auth_input"
                    >
                      <option value="">-- வகையைத் தேர்ந்தெடுக்கவும் --</option>
                      {categories.map(cat => (
                        <option key={cat.categoryName} value={cat.categoryName}>
                          {cat.description}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {selectedCategory?.subcategories?.length > 0 && (
                  <div className="auth_element">
                    <br />

                    {sortedSubcategories.map(sub => {
                      const selected = selectedSubcategories.find(s => s.subcategory === sub.name) || {};

                      // Define this near where you're mapping subcategories
                      const matchedDonations = searchResult?.donations?.filter(d =>
                        d.category === selectedCategory?.description &&
                        d.subcategory === sub.description &&
                        (
                          sub.uniqueOption === 'familyId' ||
                          sub.uniqueOption === 'familyId_date' ||
                          d.familyMemberFirstName === selectedMember?.firstName
                        )
                      ) || [];

                      const isYearFormat = /^\d{4}$/.test(sub.name);

                      const allPaidYears = (
                        searchResult?.donations?.filter(d =>
                          d.category === selectedCategory?.description &&
                          d.subcategory &&
                          /^\d{4}$/.test(d.subcategory) &&
                          sub.uniqueOption === 'familyId'
                        ).map(d => parseInt(d.subcategory, 10))
                      ) || [];

                      const maxPaidYear = allPaidYears.length > 0 ? Math.max(...allPaidYears) : null;

                      // Calculate total
                      const totalDonated = matchedDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

                      // Check if donation is allowed

                      const alreadyCompleted = (() => {
                        if (sub.amount == null) return false;
                        const requiredAmount = sub.amount || Infinity;

                        // Subcategory is a year (e.g., "2021") and uniqueOption is familyId
                        if (sub.uniqueOption === 'familyId' && isYearFormat) {
                          const subYear = parseInt(sub.name, 10);

                          if (maxPaidYear && subYear < maxPaidYear) {
                            return true; // Don't allow paying for past years
                          }

                          return totalDonated > 0;
                        }

                        if (sub.uniqueOption === 'familyId') {
                          return totalDonated > 0;
                        }

                        if (sub.uniqueOption === 'familyId_date') {
                          if (sub.amount == null) return false; // Unlimited – never block
                          return totalDonated >= sub.amount;
                        }

                        return totalDonated >= requiredAmount;
                      })();




                      return (
                        <div key={sub.name} style={{ marginBottom: '0.5rem', opacity: alreadyCompleted ? 0.5 : 1 }}>
                          <label>

                            {!(alreadyCompleted) && (
                              <label className='auth_checkbox'>
                                <input
                                  type="checkbox"
                                  value={sub.name}
                                  checked={!!selected.subcategory}
                                  onChange={(e) => handleSubcategoryChange(sub, e.target.checked)}
                                  disabled={alreadyCompleted}

                                />

                                <span className="auth_checkbox-label">{sub.description}</span>
                              </label>

                            )}

                            {selected.subcategory && !alreadyCompleted && (
                              <div style={{ marginLeft: '1rem' }}>

                                <input
                                  type="number"
                                  placeholder="தொகை"
                                  value={selected.amount}
                                  onChange={(e) => handleAmountChange(sub.name, e.target.value)}
                                  style={{ width: '100px' }}
                                />
                                {/* ✅ Self-sponsored checkbox */}
                                {sub.allowSelfSponsored && (
                                  <label style={{ marginLeft: '1rem', display: 'block' }}>
                                    <input
                                      type="checkbox"
                                      checked={!!selected.isSelfSponsored}
                                      onChange={(e) => toggleSelfSponsored(sub.name, e.target.checked)}
                                    />
                                    தனியுரிமையாக செலுத்தப்பட்டது
                                  </label>
                                )}
                              </div>
                            )}

                            {matchedDonations.length > 0 && (

                              <>
                                {alreadyCompleted && sub.uniqueOption !== 'familyId' && (<h4>{selectedCategory.description + ' ' + sub.description}</h4>)}

                                <div style={{ color: 'green', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                                  {matchedDonations.map((txn, idx) => (
                                    <div key={idx}>

                                      {alreadyCompleted && sub.uniqueOption === 'familyId' && sub.description + ':'} {txn.familyMemberFirstName} - {new Date(txn.transactionDate?._seconds * 1000).toLocaleDateString('ta-IN')} அன்று செலுத்தியது: ₹{txn.amount}
                                    </div>
                                  ))}
                                </div>
                                {sub.uniqueOption !== 'familyId' && (
                                  <strong>செலுத்தப்பட்ட மொத்த பணம்: ₹{totalDonated}</strong>
                                )}
                              </>
                            )}
                            {sub.uniqueOption !== 'familyId' && (<hr />)}

                          </label>
                        </div>

                      );
                    })}
                  </div>
                )}

                {selectedSubcategories.length > 0 && (
                  <div className="auth_element" style={{ marginTop: "1rem" }}>
                    <strong style={{ color: 'red' }}>
                      மொத்த நன்கொடை தொகை: ₹{" "}
                      {selectedSubcategories.reduce((sum, sub) => sum + (parseFloat(sub.amount) || 0), 0)}
                    </strong>
                  </div>
                )}

                <div className="auth_element">
                  <label>நன்கொடை தேதி:</label>
                  <input
                    type="date"
                    value={donationDate}
                    onChange={e => setDonationDate(e.target.value)}
                  />
                </div>

                <div className="auth_element">
                  <label>பரிவர்த்தனை முறை:</label>

                  <div>
                    <label>
                      <input
                        type="radio"
                        name="transactionMode"
                        value="cash"
                        checked={transactionMode === "cash"}
                        onChange={e => setTransactionMode(e.target.value)}
                      />
                      Cash
                    </label>
                  </div>
                  <div>
                    <label>
                      <input
                        type="radio"
                        name="transactionMode"
                        value="UPI"
                        checked={transactionMode === "UPI"}
                        onChange={e => setTransactionMode(e.target.value)}
                      />
                      UPI (GPay/PhonePay)
                    </label>
                  </div>
                </div>


                <div className="auth_element">
                  <label>பரிவர்த்தனை குறிப்பு:</label>
                  <input
                    type="text"
                    value={externalTransactionReference}
                    onChange={e => setExternalTransactionReference(e.target.value)}
                  />
                </div>

                <div className="auth_element" style={{ marginTop: "1rem" }}>
                  {loading ? (
                    <Loading />
                  ) : (
                    <button onClick={handleAddDonation} disabled={loading}
                      className={`auth_submit ${loading ? "disabled_btn" : ""}`}>
                      {loading ? 'சமர்ப்பிக்கிறது...' : 'நன்கொடை சேர்க்கவும்'}
                    </button>
                  )}
                </div>


              </>
            )}

            {showConfirmationModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h3>நீங்கள் உறுதியாக நன்கொடை சேர்க்க விரும்புகிறீர்களா?</h3>
                  {selfSponsoredWarning && (
                    <p style={{ color: 'red' }}>
                      ⚠️ சில துணை வகைகள் தனியுரிமையாக செலுத்த அனுமதிக்கின்றன, ஆனால் நீங்கள் தேர்வு செய்யவில்லை. நீங்கள் தொடர விரும்புகிறீர்களா?
                    </p>
                  )}
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      onClick={async () => {
                        setShowConfirmationModal(false);
                        setLoading(true);
                        try {
                          const response = await createDonation(pendingDonationData);
                          if (response === 409) {
                            alert("❗ Donation already exists.");
                            navigate(`/donation/view/${familyId}`);
                            return;
                          }

                          if (response) {
                            alert("✅ Donation added successfully!");
                            navigate(`/donation/view/${familyId}`);
                          }
                        } catch (err) {
                          console.error("Error adding donation:", err);
                          alert("⚠️ Something went wrong while adding the donation.");
                        } finally {
                          setLoading(false);
                          setPendingDonationData(null);
                        }
                      }}
                      style={{ marginRight: '1rem' }}
                    >
                      ஆம், தொடரவும்
                    </button>
                    <button onClick={() => setShowConfirmationModal(false)}>இல்லை, திரும்பவும்</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default DonationCreate;
